import React, { createContext, useContext, useState, useEffect } from 'react'
import settingsService from '../services/settingsService'
import logoIconFallback from '../assets/logo-icon.svg'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    logoUrl: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    accentColor: '#F59E0B',
    textColor: '#1F2937',
    backgroundColor: '#FFFFFF',
    navbarStyle: 'light',
    footerStyle: 'dark',
    buttonRadius: 'rounded',
    fontFamily: 'Inter',
    companyName: 'Himachal Yatra',
  })

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTheme()
  }, [])

  // Dynamically set favicon from theme logo or fallback
  useEffect(() => {
    const faviconUrl = theme.logoUrl || logoIconFallback
    const link =
      document.querySelector("link[rel='icon']") ||
      document.querySelector("link[rel='shortcut icon']")
    if (link) link.href = faviconUrl
  }, [theme.logoUrl])

  const loadTheme = async () => {
    try {
      const response = await settingsService.getPublicSettings()
      if (response.success && response.settings) {
        const settings = response.settings
        setTheme({
          logoUrl: settings.company_logo_url || '',
          primaryColor: settings.theme_primary_color || '#3B82F6',
          secondaryColor: settings.theme_secondary_color || '#10B981',
          accentColor: settings.theme_accent_color || '#F59E0B',
          textColor: settings.theme_text_color || '#1F2937',
          backgroundColor: settings.theme_background_color || '#FFFFFF',
          navbarStyle: settings.theme_navbar_style || 'light',
          footerStyle: settings.theme_footer_style || 'dark',
          buttonRadius: settings.theme_button_radius || 'rounded',
          fontFamily: settings.theme_font_family || 'Inter',
          companyName: settings.company_name || 'Himachal Yatra',
        })
        applyThemeToDOM(settings)
      }
    } catch (error) {
      console.error('Error loading theme:', error)
    } finally {
      setLoading(false)
    }
  }

  const applyThemeToDOM = (settings) => {
    const root = document.documentElement

    // Apply CSS variables
    root.style.setProperty('--color-primary', settings.theme_primary_color || '#3B82F6')
    root.style.setProperty('--color-secondary', settings.theme_secondary_color || '#10B981')
    root.style.setProperty('--color-accent', settings.theme_accent_color || '#F59E0B')
    root.style.setProperty('--color-text', settings.theme_text_color || '#1F2937')
    root.style.setProperty('--color-background', settings.theme_background_color || '#FFFFFF')

    // Apply button radius class
    const buttonRadius = settings.theme_button_radius || 'rounded'
    root.setAttribute('data-button-radius', buttonRadius)
  }

  const updateTheme = (newTheme) => {
    setTheme((prev) => ({ ...prev, ...newTheme }))
    applyThemeToDOM({ ...theme, ...newTheme })
  }

  const refreshTheme = () => {
    loadTheme()
  }

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, refreshTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  )
}
